// Thin API client. The access token lives in memory (never localStorage — the
// refresh token is an HttpOnly cookie the browser sends to /api/auth/*). On a
// 401 we transparently try one refresh, then retry the original request.

let accessToken: string | null = null;

export function setAccessToken(t: string | null) {
  accessToken = t;
}
export function getAccessToken() {
  return accessToken;
}

export class ApiError extends Error {
  status: number;
  data: any;
  constructor(status: number, message: string, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

async function raw(path: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  return fetch(path, { ...options, headers, credentials: 'same-origin' });
}

/** Exchange the refresh cookie for a fresh access token. Returns it or null. */
export async function refreshSession(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
    if (!res.ok) return null;
    const data = await res.json();
    accessToken = data.accessToken || null;
    return accessToken;
  } catch {
    return null;
  }
}

export async function api<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  let res = await raw(path, options);

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    const t = await refreshSession();
    if (t) res = await raw(path, options);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, (data && data.error) || `Request failed (${res.status})`, data);
  }
  return data as T;
}

export const apiGet = <T = any>(p: string) => api<T>(p);
export const apiPost = <T = any>(p: string, body?: unknown) =>
  api<T>(p, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
export const apiPatch = <T = any>(p: string, body?: unknown) =>
  api<T>(p, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
export const apiPut = <T = any>(p: string, body?: unknown) =>
  api<T>(p, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
export const apiDelete = <T = any>(p: string) => api<T>(p, { method: 'DELETE' });
